import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-none text-base font-normal uppercase ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-disabled disabled:text-primary-foreground [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary-hover",
        small:
          "bg-primary text-primary-foreground hover:bg-primary-hover",
        outline:
          "border border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground disabled:border-disabled disabled:text-disabled disabled:hover:bg-transparent",
        outlineLight:
          "border border-secondary-foreground bg-transparent text-secondary-foreground hover:bg-secondary-foreground hover:text-primary disabled:border-disabled disabled:text-disabled disabled:hover:bg-transparent",
        link: "normal-case text-link text-foreground underline underline-offset-4 hover:text-primary disabled:no-underline",
        linkArrow:
          "gap-2 uppercase text-link text-foreground hover:text-primary disabled:text-disabled",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "normal-case hover:bg-accent hover:text-accent-foreground",
        foreground: "text-background bg-foreground/80 hover:bg-foreground",
      },
      size: {
        default: "min-h-[47px] px-6 py-3.5",
        sm: "min-h-8 px-4 py-2 text-xs",
        lg: "h-11 px-8",
        icon: "size-10",
        link: "h-auto min-h-0 p-0",
      },
    },
    compoundVariants: [
      { variant: "link", size: "default", class: "h-auto min-h-0 p-0" },
      { variant: "linkArrow", size: "default", class: "h-auto min-h-0 p-0" },
      { variant: "small", size: "default", class: "min-h-8 px-4 py-2 text-xs" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export type ButtonSizeType = "default" | "sm" | "lg" | "icon" | "link";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const resolvedVariant = variant ?? "default";
    const resolvedSize =
      resolvedVariant === "link" || resolvedVariant === "linkArrow"
        ? "link"
        : size;

    return (
      <Comp
        className={cn(
          buttonVariants({ variant: resolvedVariant, size: resolvedSize, className })
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
